import { Alert, Box, Button, Container, CssBaseline, TextField, ThemeProvider, useTheme } from '@mui/material';
import { useState, type FormEvent } from 'react';
import axios from 'axios';
import { useAuth } from 'src/util/authenticationProvider';
import { PrizeDto } from 'src/util/types';
import config from '../../../../config';
import { useTranslation } from 'react-i18next';

type PrizeEditProps = PrizeDto & {
  onSuccess?: (updatedPrize: PrizeDto) => void; // eslint-disable-line @typescript-eslint/no-unused-vars, no-unused-vars
};

export default function PrizeEdit(props: PrizeEditProps){
  const authContext = useAuth();
  const theme = useTheme();
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  const {t} = useTranslation();
  const fileToBase64 = async (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
   
  const handleSubmitData = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    const formData = new FormData(event.currentTarget);
    const name = (formData.get('name') as string) || props.name || '';
    const description = (formData.get('description') as string) || props.description || '';

    let imageBase64 = props.image;
    const imageInput = imageFile || (formData.get('image') as File | null);
    if (imageInput instanceof File && imageInput.size > 0) {
      try {
        imageBase64 = await fileToBase64(imageInput);
      } catch {
        setError('Bild konnte nicht gelesen werden.');
        return;
      }
    }

    if (!imageBase64) {
      setError('Image is required');
      return;
    }

    try {
      const response = await axios.put(config.apiUrl + `/api/prize/update/${encodeURIComponent(props.id)}`, {
        name,
        description,
        image: imageBase64
      }, {
        headers: {
          "Authorization": "Bearer " + authContext.tokens.accessToken,
          "Content-Type": "application/json"
        }
      });
      const updatedPrize: PrizeDto =
        (response.data && (response.data.content as PrizeDto)) ??
        { id: props.id, name, description, image: imageBase64 };

      props.onSuccess?.(updatedPrize);
    } catch {
      setError('Connection to Server failed.');
    }
  }

  return(
    <>
      <ThemeProvider theme={theme}>
    <Container component="main" maxWidth="lg">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box component="form" onSubmit={handleSubmitData} noValidate sx={{ mt: 1 }}>
        <TextField
            margin="normal"
            fullWidth
            id="name"
            label={t('prizes.nameLabel')}
            name="name"
            autoComplete="name"
            defaultValue={props.name}
            autoFocus
          />
        <TextField
            margin="normal"
            fullWidth
            id="description"
            label={t('field.Description')}
            name="description"
            autoComplete="description"
            defaultValue={props.description}
          />
          <TextField
            margin="normal"
            type="file"
            fullWidth
            id="image"
            name="image"
            inputProps={{ accept: 'image/*' }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              const target = e.target as HTMLInputElement;
              setImageFile(target.files?.[0] ?? null);
            }}
          />
        
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            {t('general.Apply')}
          </Button>
        </Box>
        {error ? <Alert severity="error">{error}</Alert> : ''}
        {/* Success is handled by parent to show outside the dialog */}
      </Box>
    </Container>
  </ThemeProvider>
    </>
  );
}
